import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import AdminSchemaBasedEditor from "./AdminSchemaBasedEditor";

// Help content component for Steps
const StepsHelpContent = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Overview</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        Steps define AI processing tasks. Each step references a prompt and specifies how data flows 
        through the document processing pipeline.
      </p>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Step Types</h4>
      <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <p><strong>AI Processing</strong> - Runs a prompt through Vertex AI to extract/generate content</p>
        <p><strong>Empty</strong> - Creates a placeholder for user input only (no AI processing)</p>
        <p><strong>Continue</strong> - Signals that main document processing is complete</p>
        <p><strong>History</strong> - Pulls data from patient's previous documents</p>
      </Box>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Patient Context</h4>
      <p style={{ margin: '0 0 8px 0', color: 'var(--color-text-secondary)' }}>
        These options control what patient information is included in the AI context:
      </p>
      <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <p><strong>Include Patient Name</strong> - Add patient's name to the prompt</p>
        <p><strong>Include Patient Data</strong> - Add demographics and stored patient info</p>
        <p><strong>Include Field History</strong> - Add previous values of specific fields</p>
      </Box>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Processing Options</h4>
      <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <p><strong>Priority</strong> - Priority steps show in the progress UI and run first</p>
        <p><strong>Allow Grouping</strong> - Step can be combined with others for efficiency</p>
        <p><strong>Post-Processing</strong> - Additional parsing applied to AI response</p>
      </Box>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Progress Messaging</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        For priority steps, configure the title and details shown in the progress indicator 
        while this step is processing.
      </p>
    </Box>

    <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', borderTop: '1px solid var(--color-border)', pt: 2 }}>
      Full documentation: <code>docs/admin-document-builder.md</code>
    </Box>
  </Box>
);

// Configuration for the Steps editor
const stepsConfig = {
  collectionName: "documentSteps",
  schemaKey: "stepSchema",
  entityName: "Step",
  entityNamePlural: "Steps",
  idPlaceholder: "chiefComplaint",
  columns: [
    {
      key: "displayName",
      label: "Name",
      render: (item) => (
        <span className="table-primary-text" style={{ color: 'var(--color-primary)' }}>{item.displayName || item.name || item.id}</span>
      )
    },
    {
      key: "type",
      label: "Type",
      render: (item) => {
        const type = item.type || "unknown";
        const colorMap = {
          "submitToVertexAI": "primary",
          "empty": "default",
          "continue": "secondary",
          "history": "info"
        };
        const labelMap = {
          "submitToVertexAI": "AI Processing",
          "empty": "Empty",
          "continue": "Continue",
          "history": "History"
        };
        return (
          <Chip 
            label={labelMap[type] || type}
            size="small"
            color={colorMap[type] || "default"}
            variant="outlined"
          />
        );
      }
    },
    {
      key: "output",
      label: "Output Field",
      render: (item) => (
        <span style={{ fontFamily: 'monospace', fontSize: 'var(--text-sm)' }}>
          {item.output || "-"}
        </span>
      )
    },
    {
      key: "status",
      label: "Status",
      sortValue: (item) => {
        const version = item.version || 1;
        const publishedVersion = item.publishedVersion;
        return (publishedVersion && publishedVersion === version) ? "published" : "draft";
      },
      render: (item) => {
        const version = item.version || 1;
        const publishedVersion = item.publishedVersion;
        const isPublished = publishedVersion && publishedVersion === version;
        return (
          <Tooltip title={isPublished ? `Published v${publishedVersion}` : `Draft v${version}`} arrow>
            <Chip 
              label={isPublished ? "Published" : "Draft"}
              size="small"
              color={isPublished ? "success" : "warning"}
              variant="outlined"
            />
          </Tooltip>
        );
      }
    }
  ],
  HelpContent: StepsHelpContent
};

const AdminStepsEditor = () => {
  return <AdminSchemaBasedEditor config={stepsConfig} />;
};

export default AdminStepsEditor;